// Small helper that hits the FastAPI server using the customer's
// Supabase JWT in x-user-token. The API decodes the JWT to figure out
// the customer_id.

import { supabaseConfig } from './config';

// In dev (Vite server with proxy), use the same-origin path so the
// proxy can rewrite /api/* to FastAPI. In production (Cloudflare Pages),
// the operator should set VITE_API_BASE to the Railway service URL —
// the env var is a full URL like "https://datames-production.up.railway.app"
// that we use verbatim (no /api prefix added).
export const API_BASE_URL =
  import.meta.env.VITE_API_BASE ||
  (import.meta.env.DEV ? '/api' : '');

// If API_BASE_URL is empty, every request would 404 against the SPA
// fallback. Surface that loudly so operators see the bug at app load
// instead of "everything silently does nothing".
if (!API_BASE_URL && !import.meta.env.DEV) {
  // eslint-disable-next-line no-console
  console.error(
    '[Datames] VITE_API_BASE is not set. Set it in your Cloudflare Pages ' +
    'environment variables to the Railway service URL (e.g. ' +
    '"https://datames-production.up.railway.app").'
  );
}

export async function apiFetch(path, options = {}) {
  // path is e.g. '/payments/billing'. We send it as-is to the API base URL.
  // In dev (proxy): /api + /payments/billing = /api/payments/billing
  //   (Vite rewrites /api → '' so backend sees /payments/billing)
  // In prod (VITE_API_BASE): https://.../v1/payments/billing
  const fullPath = path.startsWith('/') ? path : `/${path}`;
  const sessionMod = await import('./supabaseClient');
  const { data: { session } } = await sessionMod.supabase.auth.getSession();
  const token = session?.access_token || '';
  // Auto-serialize JS objects to JSON for the body (fetch() doesn't
  // do this automatically).
  let body = options.body;
  const headers = {
    ...(token ? { 'x-user-token': token } : {}),
    ...(options.headers || {}),
  };
  if (body !== undefined && body !== null && typeof body !== 'string') {
    body = JSON.stringify(body);
    if (!('Content-Type' in headers)) headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(`${API_BASE_URL}${fullPath}`, { ...options, body, headers });
  if (!res.ok) {
    const text = await res.text();
    let parsed;
    try { parsed = JSON.parse(text); } catch { parsed = text; }
    let msg;
    if (typeof parsed?.detail === 'string') {
      msg = parsed.detail;
    } else if (parsed?.detail?.message) {
      msg = parsed.detail.message;
    } else if (parsed?.detail?.code) {
      msg = `${parsed.detail.code}: ${parsed.detail.message || ''}`;
    } else if (Array.isArray(parsed?.detail)) {
      msg = parsed.detail.map(e => e.msg || JSON.stringify(e)).join('; ');
    } else {
      msg = res.statusText;
    }
    const err = Object.assign(new Error(msg), {
      status: res.status,
      detail: parsed?.detail || text,
    });
    throw err;
  }
  return res.json();
}